/**
 * Schedule maths for compose: turn a wall-clock date and time in a chosen
 * IANA zone into the instant it names, and say that instant back in words.
 * Everything here is pure and safe to run during server rendering.
 */

/** Zones offered first, before the full list. */
const SUGGESTED_ZONES = ['Africa/Gaborone', 'Africa/Johannesburg', 'UTC'];

/** The browser's zone, or UTC when it cannot be read. */
export function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export interface ZoneGroups {
  suggested: string[];
  all: string[];
}

/**
 * Suggested zones (the browser's own first), then every zone the runtime
 * knows. `fallback` is used when the runtime cannot list its zones.
 */
export function zoneGroups(fallback: string[]): ZoneGroups {
  const suggested = Array.from(new Set([localZone(), ...SUGGESTED_ZONES])).filter(isValidZone);
  let all: string[] = [];
  try {
    const list = (Intl as any).supportedValuesOf?.('timeZone');
    if (Array.isArray(list)) all = list as string[];
  } catch {
    all = [];
  }
  if (!all.length) all = fallback;
  const rest = all.filter((zone) => !suggested.includes(zone) && isValidZone(zone));
  return { suggested, all: rest };
}

/** Milliseconds the zone's wall clock is ahead of UTC at `utcMs`. */
function zoneOffset(utcMs: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/**
 * The instant a wall-clock `date` (yyyy-mm-dd) and `time` (HH:mm) name in
 * `zone`. Null when any part is missing or invalid.
 */
export function zonedInstant(
  date: string | null | undefined,
  time: string | null | undefined,
  zone: string | null | undefined
): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const clock = /^(\d{1,2}):(\d{2})/.exec(time || '');
  if (!day || !clock || !zone || !isValidZone(zone)) return null;

  const guess = Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2]));
  if (Number.isNaN(guess)) return null;
  const first = zoneOffset(guess, zone);
  let utc = guess - first;
  // Near a daylight-saving change the offset at the answer can differ.
  const second = zoneOffset(utc, zone);
  if (second !== first) utc = guess - second;
  return new Date(utc);
}

/**
 * The zone's short name at that moment: "CAT", "SAST", "BST", "EDT". Each
 * English locale knows a different set, so try a few before settling for
 * an offset such as "GMT+2".
 */
export function zoneAbbreviation(value: Date, zone: string): string {
  let fallback = '';
  for (const locale of ['en-GB', 'en-ZA', 'en-US']) {
    try {
      const name =
        new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'short' })
          .formatToParts(value)
          .find((part) => part.type === 'timeZoneName')?.value ?? '';
      if (!fallback) fallback = name;
      if (name && !/^(GMT|UTC)[+-]/.test(name)) return name;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/** "Tue 13 Oct, 09:00 CAT" in the given zone. */
export function formatInZone(value: Date, zone: string, withZoneName = true): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(value);
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const base = `${get('weekday')} ${get('day')} ${get('month')}, ${get('hour')}:${get('minute')}`;
    const name = withZoneName ? zoneAbbreviation(value, zone) : '';
    return name ? `${base} ${name}` : base;
  } catch {
    return value.toISOString();
  }
}

/** "09:00" in the given zone. */
export function clockInZone(value: Date, zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(value);
  } catch {
    return '';
  }
}

/** Today as yyyy-mm-dd in the given zone, for the date input's `min`. */
export function todayInZone(zone: string | null | undefined): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone && isValidZone(zone) ? zone : localZone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** "Africa/Gaborone" → "Africa / Gaborone", "America/Argentina/Salta" → "America / Argentina / Salta". */
export function zoneLabel(zone: string): string {
  return zone.replace(/_/g, ' ').split('/').join(' / ');
}

export interface ScheduleSummary {
  /** The instant, or null when the schedule is incomplete. */
  at: Date | null;
  /** "Tue 13 Oct, 09:00 CAT". */
  when: string;
  /** "in 3 h 10 min". */
  countdown: string;
  /** "11:00 your time" when the chosen zone is not the browser's. */
  local: string;
  /** True once the moment has passed. */
  past: boolean;
}

export function describeSchedule(
  date: string | null | undefined,
  time: string | null | undefined,
  zone: string | null | undefined,
  countdown: (value: Date) => string,
  now = new Date()
): ScheduleSummary {
  const at = zonedInstant(date, time, zone);
  if (!at || !zone) return { at: null, when: '', countdown: '', local: '', past: false };
  const browser = localZone();
  const differs = zoneOffset(at.getTime(), zone) !== zoneOffset(at.getTime(), browser);
  return {
    at,
    when: formatInZone(at, zone),
    countdown: countdown(at),
    local: differs ? `${clockInZone(at, browser)} your time` : '',
    past: at.getTime() <= now.getTime(),
  };
}
